import { useState } from 'react';
import { Loader2, RefreshCw, Search, ShieldOff, UserX } from 'lucide-react';
import { toast } from 'sonner';

import AdminShell from '../components/AdminShell';
import { useAdminCooldown } from '../hooks/useAdminCooldown';

function daysRemaining(cooldownUntil: string): number {
  const ms = new Date(cooldownUntil).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function CooldownAccountsPage() {
  const [search, setSearch]   = useState('');
  const [page, setPage]       = useState(1);
  const pageSize              = 20;

  const { data, loading, lifting, refresh, lift } = useAdminCooldown({ page, pageSize, search: search || undefined });

  const handleLift = async (id: number, email: string) => {
    try {
      await lift(id);
      toast.success(`Cooldown lifted for ${email}`);
    } catch {
      toast.error('Failed to lift cooldown');
    }
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const pagination = data?.pagination;

  return (
    <AdminShell
      title="Cooldown Accounts"
      description="Emails blocked from re-registering within 30 days of account deletion. Remove an entry to allow immediate re-registration."
      actions={(
        <button
          onClick={() => refresh().catch(() => undefined)}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-zinc-700 text-zinc-300 hover:border-zinc-600 hover:text-zinc-100 transition-colors text-xs"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Refresh
        </button>
      )}
    >
      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
        <input
          type="text"
          placeholder="Search by email…"
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          className="w-full pl-8 pr-3 py-2 rounded-lg border border-zinc-700 bg-zinc-900 text-sm text-zinc-100 placeholder-zinc-500 focus:outline-none focus:border-zinc-500 transition-colors"
        />
      </div>

      {/* Table */}
      <section className="rounded-2xl border border-zinc-800 bg-zinc-900 overflow-hidden">
        {loading && !data ? (
          <div className="flex items-center gap-2 text-zinc-500 p-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-zinc-500">
            <UserX className="w-8 h-8 opacity-40" />
            <p className="text-sm">{search ? 'No matching cooldown records.' : 'No accounts in cooldown.'}</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800 text-xs text-zinc-500 uppercase tracking-wider">
                <th className="px-5 py-3 text-left font-medium">Email</th>
                <th className="px-5 py-3 text-left font-medium">Deleted At</th>
                <th className="px-5 py-3 text-left font-medium">Cooldown Until</th>
                <th className="px-5 py-3 text-left font-medium">Days Left</th>
                <th className="px-5 py-3 text-left font-medium">Orig. User ID</th>
                <th className="px-5 py-3 text-right font-medium">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {data.items.map((row) => {
                const days = daysRemaining(row.cooldownUntil);
                return (
                  <tr key={row.id} className="hover:bg-zinc-800/40 transition-colors">
                    <td className="px-5 py-3.5 font-mono text-zinc-100 text-xs">{row.email}</td>
                    <td className="px-5 py-3.5 text-zinc-400 text-xs whitespace-nowrap">{formatDate(row.deletedAt)}</td>
                    <td className="px-5 py-3.5 text-zinc-400 text-xs whitespace-nowrap">{formatDate(row.cooldownUntil)}</td>
                    <td className="px-5 py-3.5">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                        days === 0
                          ? 'bg-zinc-800 text-zinc-400'
                          : days <= 7
                          ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                          : 'bg-red-500/10 text-red-400 border border-red-500/20'
                      }`}>
                        {days === 0 ? 'Expired' : `${days}d`}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-zinc-500 text-xs">{row.originalUserId ?? '—'}</td>
                    <td className="px-5 py-3.5 text-right">
                      <button
                        onClick={() => handleLift(row.id, row.email)}
                        disabled={lifting === row.id}
                        className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg border border-zinc-700 text-zinc-300 hover:border-red-500/50 hover:text-red-400 transition-colors text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {lifting === row.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <ShieldOff className="w-3 h-3" />
                        )}
                        Lift
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-zinc-500">
          <span>{pagination.total} record{pagination.total !== 1 ? 's' : ''}</span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="h-7 px-3 rounded-lg border border-zinc-700 text-zinc-300 hover:border-zinc-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Prev
            </button>
            <span className="text-zinc-400">
              {page} / {pagination.totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
              disabled={page >= pagination.totalPages}
              className="h-7 px-3 rounded-lg border border-zinc-700 text-zinc-300 hover:border-zinc-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
